import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, FileSpreadsheet, Search, Trash2, Upload } from "lucide-react";
import { api } from "@/auth/AuthContext";

const detailMessage = (error) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === "string") return detail;
  const codes = detail?.duplicate_codes || detail?.unmatched_codes || [];
  return `${detail?.message || "Proses sasaran PKS gagal."}${codes.length ? ` Kode PT: ${codes.join(", ")}.` : ""}`;
};

export const AgreementTargetManager = ({ taskId, onTargetsChanged }) => {
  const [targets, setTargets] = useState({ active: [], cancelled: [] });
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const response = await api.get(`/regular-tasks/agreement/${taskId}/targets`);
    setTargets(response.data);
    onTargetsChanged?.(response.data.active);
  }, [onTargetsChanged, taskId]);
  useEffect(() => { load().catch((err) => setError(detailMessage(err))); }, [load]);

  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    return term ? targets.active.filter((item) => `${item.kodept} ${item.namapt}`.toLowerCase().includes(term)) : targets.active;
  }, [query, targets.active]);

  const previewFile = async (nextFile) => {
    setFile(nextFile || null); setPreview(null); setError(""); setNotice("");
    if (!nextFile) return;
    const form = new FormData(); form.append("file", nextFile);
    setBusy(true);
    try {
      const response = await api.post(`/regular-tasks/agreement/${taskId}/targets/preview`, form);
      setPreview(response.data);
    } catch (err) {
      setFile(null); setError(detailMessage(err));
      if (fileRef.current) fileRef.current.value = "";
    } finally { setBusy(false); }
  };

  const addTargets = async () => {
    if (!file || !preview) return;
    const form = new FormData(); form.append("file", file);
    setBusy(true); setError("");
    try {
      const response = await api.post(`/regular-tasks/agreement/${taskId}/targets`, form);
      setNotice(`${response.data.added_count} PT ditambahkan${response.data.reactivated_count ? `, termasuk ${response.data.reactivated_count} PT yang diaktifkan kembali` : ""}.`);
      setFile(null); setPreview(null); if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (err) { setError(detailMessage(err)); }
    finally { setBusy(false); }
  };

  const cancelTargets = async () => {
    if (!confirm) return;
    setBusy(true); setError("");
    try {
      const response = await api.post(`/regular-tasks/agreement/${taskId}/targets/cancel`, {
        pt_ids: confirm.removeAll ? [] : confirm.ptIds,
        remove_all: confirm.removeAll,
      });
      setNotice(`${response.data.cancelled_count} PT dibatalkan dari sasaran Task.`);
      setSelected(new Set()); setConfirm(null); await load();
    } catch (err) { setError(detailMessage(err)); }
    finally { setBusy(false); }
  };

  const toggle = (ptId) => setSelected((current) => {
    const next = new Set(current); next.has(ptId) ? next.delete(ptId) : next.add(ptId); return next;
  });

  return (
    <section className="agreement-target-manager" data-testid="pks-target-manager">
      <div className="eyebrow"><FileSpreadsheet size={13} /> SASARAN PERGURUAN TINGGI</div>
      {notice && <div className="info-banner" data-testid="pks-target-notice"><CheckCircle2 size={14} /> {notice}</div>}
      {error && <div className="error-banner" data-testid="pks-target-error"><AlertTriangle size={14} /> {error}</div>}

      <div className="agreement-target-upload">
        <div><strong>Tambah Sasaran melalui Excel</strong><p className="muted-text">Gunakan format template Detail Perjanjian. Seluruh file ditolak jika satu Kode PT masih menjadi sasaran aktif.</p></div>
        <label className="download-button ghost" data-testid="pks-target-file-trigger"><Upload size={13} /> {busy ? "Memeriksa…" : "Pilih Excel"}<input ref={fileRef} type="file" accept=".xlsx" hidden data-testid="pks-target-file" onChange={(event) => previewFile(event.target.files?.[0])} /></label>
      </div>
      {preview && <div className="agreement-target-preview" data-testid="pks-target-preview">
        <strong>{preview.total_rows} PT siap ditambahkan</strong>
        <span>{preview.new_count} baru · {preview.reactivated_count} diaktifkan kembali</span>
        <button type="button" className="download-button" data-testid="pks-target-add-confirm" disabled={busy} onClick={addTargets}>{busy ? "Menambahkan…" : "Tambahkan Sasaran"}</button>
      </div>}

      <div className="agreement-target-list-head">
        <div><strong>{targets.active.length} PT sasaran aktif</strong>{targets.cancelled.length > 0 && <span>{targets.cancelled.length} riwayat dibatalkan</span>}</div>
        <div className="search-wrap"><Search size={13} /><input value={query} data-testid="pks-target-search" onChange={(event) => setQuery(event.target.value)} placeholder="Cari kode atau nama PT…" /></div>
      </div>
      <div className="agreement-target-list" data-testid="pks-target-active-list">
        {visible.map((item) => <label key={item.pt_id} data-testid={`pks-target-${item.pt_id}`}><input type="checkbox" checked={selected.has(item.pt_id)} onChange={() => toggle(item.pt_id)} data-testid={`pks-target-select-${item.pt_id}`} /><span><strong>{item.namapt}</strong><small>{item.kodept}</small></span></label>)}
        {!visible.length && <div className="empty-state">Tidak ada sasaran aktif.</div>}
      </div>
      <div className="agreement-target-actions">
        <button type="button" className="download-button task-close-button" data-testid="pks-target-cancel-selected" disabled={!selected.size || busy} onClick={() => setConfirm({ removeAll: false, ptIds: [...selected] })}><Trash2 size={13} /> Batalkan {selected.size || ""} Terpilih</button>
        <button type="button" className="download-button ghost" data-testid="pks-target-cancel-all" disabled={!targets.active.length || busy} onClick={() => setConfirm({ removeAll: true, ptIds: [] })}>Batalkan Semua Sasaran</button>
      </div>
      {confirm && <div className="draft-confirm" data-testid="pks-target-cancel-confirmation"><AlertTriangle size={18} /><div><strong>{confirm.removeAll ? "Batalkan seluruh sasaran?" : `Batalkan ${confirm.ptIds.length} sasaran terpilih?`}</strong><p>Task akan berpindah ke Arsip Task PT dengan status DIBATALKAN. Draft dan submission tetap tersimpan sebagai riwayat read-only.</p><div className="draft-confirm-actions"><button type="button" className="download-button ghost" data-testid="pks-target-cancel-back" onClick={() => setConfirm(null)}>Kembali</button><button type="button" className="download-button task-close-button" data-testid="pks-target-cancel-confirm" disabled={busy} onClick={cancelTargets}>{busy ? "Membatalkan…" : "Ya, Batalkan Sasaran"}</button></div></div></div>}
    </section>
  );
};